import { useMemo, type JSX } from 'react';
import { useTranslation } from '../../shared/i18n';
import { Icon, type IconName } from '../../shared/ui/Icon';
import { RTT_CHART_HEIGHT, RTT_CHART_WIDTH, rttGeometry } from './rtt';
import type { RttView } from './rttPreferences';
import { useMonitorHistory } from './api';

/**
 * Мини-график времени отклика за час (§11, фаза 4, задача 5).
 *
 * Шкала своя у каждого монитора: рядом стоят ICMP с откликом в доли
 * миллисекунды и HTTP с сотнями, и общая шкала прижала бы первый к нулю.
 * Поэтому подписан пик — без него высота линии ничего не значит.
 *
 * Видов два — столбики и линия, — и переключатель стоит тут же, у графика:
 * это не настройка платформы, которую ищут в разделе настроек, а способ
 * посмотреть на то, на что человек смотрит прямо сейчас. Выбор общий на все
 * графики и запоминается (см. `rttPreferences`).
 *
 * График сидит в своей рамке, как в макете (раздел 03): без неё столбики
 * висят в воздухе среди строк текста, и непонятно, где у графика низ.
 */
export function RttChart({
  monitorId,
  everChecked,
  view,
  onViewChange,
}: {
  monitorId: string;
  /**
   * Проверялся ли монитор хоть раз.
   *
   * Различает два пустых окна, которые выглядят одинаково, но означают
   * разное: у нового монитора истории ещё нет, а у монитора отвалившейся
   * площадки она есть — просто вся старше часа.
   */
  everChecked: boolean;
  view: RttView;
  onViewChange: (view: RttView) => void;
}): JSX.Element | null {
  const { t } = useTranslation();

  const history = useMonitorHistory(monitorId);
  const points = history.data?.points ?? [];

  const geometry = useMemo(() => rttGeometry(points), [points]);

  if (history.isPending || (points.length === 0 && !everChecked)) {
    // Пустой график занимает место и ничего не сообщает: у нового монитора
    // истории ещё нет, у выключенного её больше не будет.
    return null;
  }

  if (points.length === 0) {
    /*
      Проверки были, но не в этом часе — так выглядит площадка, чей агент
      пропал. Исчезнувший график читался бы как поломка интерфейса, а
      «за последний час проверок не было» говорит ровно то, что произошло,
      и оставляет на месте рамку: вернутся данные — ничего не прыгнет.
    */
    return (
      <div className="flex flex-col gap-1 rounded-xs border border-line bg-raised px-2 pt-1.5 pb-1">
        <div className="flex h-9 items-center justify-center">
          <span className="font-mono text-2xs text-faint">{t('monitors.chart.noRecentData')}</span>
        </div>
        <span className="truncate font-mono text-2xs text-faint">{t('monitors.chart.hour')}</span>
      </div>
    );
  }

  const peak =
    geometry.peakMs === null
      ? ''
      : t('monitors.chart.peakSuffix', { value: geometry.peakMs.toFixed(1) });

  return (
    <div className="flex flex-col gap-1 rounded-xs border border-line bg-raised px-2 pt-1.5 pb-1">
      <svg
        viewBox={`0 0 ${RTT_CHART_WIDTH} ${RTT_CHART_HEIGHT}`}
        preserveAspectRatio="none"
        className="h-9 w-full"
        role="img"
        aria-label={t('monitors.chart.label', {
          peak,
          view: view === 'bars' ? t('monitors.chart.bars') : t('monitors.chart.line'),
        })}
      >
        {view === 'bars' ? (
          geometry.bars.map((bar) => (
            <rect
              key={bar.x}
              x={bar.x}
              y={bar.y}
              width={bar.width}
              height={bar.height}
              /*
                Цвет здесь — состояние, а не украшение (§12): красный столбик
                и есть то, ради чего на график смотрят. Зелёный приглушён,
                иначе полсотни ярких столбиков перекрикивают единственный
                красный — тот самый, который нужно заметить.
              */
              fill={bar.failed ? 'var(--color-down)' : 'var(--color-up)'}
              opacity={bar.failed ? 0.85 : 0.55}
            />
          ))
        ) : (
          <>
            {geometry.failures.map((failure) => (
              <rect
                key={failure.x}
                x={failure.x}
                y={0}
                width={failure.width}
                height={RTT_CHART_HEIGHT}
                fill="var(--color-down)"
                opacity={0.25}
              />
            ))}

            {geometry.line ? (
              <path
                d={geometry.line}
                fill="none"
                stroke="var(--color-info)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
              />
            ) : null}
          </>
        )}
      </svg>

      <div className="flex items-center gap-2">
        <span className="truncate font-mono text-2xs text-faint">
          {geometry.peakMs === null
            ? t('monitors.chart.hour')
            : t('monitors.chart.hourPeak', { value: geometry.peakMs.toFixed(1) })}
        </span>

        <div className="ml-auto flex flex-none overflow-hidden rounded-xs border border-line-strong">
          <ViewButton
            icon="chartBars"
            label={t('monitors.chart.bars')}
            active={view === 'bars'}
            onSelect={() => onViewChange('bars')}
          />
          <ViewButton
            icon="chartLine"
            label={t('monitors.chart.line')}
            active={view === 'line'}
            onSelect={() => onViewChange('line')}
          />
        </div>
      </div>
    </div>
  );
}

/** Кнопка вида графика. Подпись — в подсказке: рядом с графиком места нет. */
function ViewButton({
  icon,
  label,
  active,
  onSelect,
}: {
  icon: IconName;
  label: string;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      aria-label={label}
      title={label}
      className={`flex h-5 w-6 items-center justify-center ${
        active ? 'bg-overlay text-fg' : 'text-dim hover:text-fg'
      }`}
    >
      <Icon name={icon} />
    </button>
  );
}
